import { BASE_URL } from "../App_Settings/app_settings";
import {
  getUserIdFromLocalStorage,
  deleteFile,
  logFileProcess,
} from "../API_Endpoints/APIEndpoints";
import { NotificationService } from "./notificationService";

/**
 * Centralized Delete Service
 * Handles file deletion across all screens (Workload Import, Extraction Review, Approval Review, Rejections, Export)
 * Notifications are handled internally using the NotificationService from constants
 */

interface DeleteFileParams {
  fileImportId: string;
  fileName: string;
  rows: any[];
  setIsDeleting: (value: boolean) => void;
  handleNotificationList?: (notifications: any[]) => void;
  notifications?: any[];
  getAllFiles: () => void;
  setIsPopupOpen?: (value: boolean) => void;
}

/**
 * Delete a file from the system
 * @param params - Delete file parameters
 * @returns Promise<boolean> - Returns true if deletion was successful, false otherwise
 */
export const handleFileDelete = async (params: DeleteFileParams): Promise<boolean> => {
  const {
    fileImportId,
    fileName,
    rows,
    setIsDeleting,
    handleNotificationList,
    notifications,
    getAllFiles,
    setIsPopupOpen,
  } = params;

  if (!fileImportId) {
    console.error("No file ID provided for deletion");
    return false;
  }

  // Close popup if function is provided
  if (setIsPopupOpen) {
    setIsPopupOpen(false);
  }

  setIsDeleting(true);

  // Find the row being deleted
  const rowToDelete = rows.find((row) => row.fileImportId === fileImportId);

  try {
    if (!rowToDelete) {
      throw new Error("File not found");
    }

    // Make the DELETE request
    const userData = getUserIdFromLocalStorage();
    const response = await fetch(`${BASE_URL}/FileData/ImportFiles/${fileImportId}`, {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${userData.token}`,
        accept: "*/*",
      },
    });

    if (!response.ok) {
      throw new Error(`Error: ${response.status}`);
    }

    // Delete related files (Excel, XML, PDF) from storage
    const fileExtension = rowToDelete.fileName.split(".").pop().toLowerCase();

    await deleteFile(
      userData.organizationId,
      userData.userId,
      fileImportId,
      fileExtension,
      rowToDelete.businessUnit
    );

    // Log the deletion
    await logFileProcess({
      fileName: rowToDelete.fileName,
      fileImportId: fileImportId,
      uploadDate: null,
      extractedDate: null,
      approvedDate: null,
      rejectedDate: null,
      exportedDate: null,
      deletedDate: new Date(),
    });

    // Add success notification
    const deleteNotification = NotificationService.deleteSuccess(rowToDelete.fileName);
    if (handleNotificationList && notifications)
      handleNotificationList([...notifications, deleteNotification]);

    // Refresh the file list
    getAllFiles();

    return true;
  } catch (error: any) {
    console.error("Error deleting file:", error);

    // Add error notification
    const errorNotification = NotificationService.deleteError(
      rowToDelete?.fileName || fileName || "Unknown file",
      error.message || "An error occurred while deleting the file"
    );
    if (handleNotificationList && notifications)
      handleNotificationList([...notifications, errorNotification]);

    alert("Failed to delete file. Please try again.");

    return false;
  } finally {
    setIsDeleting(false);
  }
};

/**
 * Delete multiple files in bulk
 * @param fileIds - Array of file IDs to delete
 * @param rows - All rows data
 * @param setIsDeleting - State setter for deletion loading
 * @param handleNotificationList - Notification handler
 * @param notifications - Current notifications
 * @param getAllFiles - Function to refresh file list
 * @returns Promise<{success: number, failed: number}>
 */
export const handleBulkFileDelete = async (
  fileIds: string[],
  rows: any[],
  setIsDeleting: (value: boolean) => void,
  handleNotificationList?: (notifications: any[]) => void,
  notifications?: any[],
  getAllFiles?: () => void
): Promise<{ success: number; failed: number }> => {
  if (!fileIds || fileIds.length === 0) {
    return { success: 0, failed: 0 };
  }

  setIsDeleting(true);

  let successCount = 0;
  let failedCount = 0;

  try {
    // Process deletions in parallel
    const deletePromises = fileIds.map(async (fileId) => {
      const row = rows.find((r) => r.fileImportId === fileId);
      if (!row) {
        failedCount++;
        return false;
      }

      const result = await handleFileDelete({
        fileImportId: fileId,
        fileName: row.fileName,
        rows,
        setIsDeleting: () => {}, // Don't update state for individual deletions
        handleNotificationList,
        notifications,
        getAllFiles: () => {}, // Don't refresh for individual deletions
      });

      if (result) {
        successCount++;
      } else {
        failedCount++;
      }

      return result;
    });

    await Promise.all(deletePromises);

    if (getAllFiles) getAllFiles();

    // Show summary notification
    if (successCount > 0) {
      const summaryNotification = NotificationService.deleteSuccess(
        `${successCount} file(s) deleted successfully${failedCount > 0 ? `, ${failedCount} failed` : ""}`
      );
      if (handleNotificationList && notifications)
        handleNotificationList([...notifications, summaryNotification]);
    }

    return { success: successCount, failed: failedCount };
  } catch (error: any) {
    console.error("Error in bulk delete:", error);
    return { success: successCount, failed: failedCount };
  } finally {
    setIsDeleting(false);
  }
};
